import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { createElement } from 'react';
import { Card, Text, Icons, styled, Stack, Box, useTheme } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';

export interface Tip {
  id: string;
  text: string;
  icon: string;
}

export interface HelpfulTipsProps {
  tips: Tip[];
}

const Title = styled(Text)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  gap: theme.spacing(1),
  padding: theme.spacing(0.25),
  width: 'fit-content',
  borderRadius: '4px',
  fontWeight: 'bold',
}));

const StyledIcon = (
  icon: OverridableComponent<SvgIconTypeMap<Record<string, unknown>, 'svg'>> & {
    muiName: string;
  }
) =>
  styled(icon)(() => ({
    width: '20px',
    height: '20px',
  }));

/**
 * This component displays a list of helpful tips where each tip is a bullet point.
 */
export function HelpfulTips({ tips }: HelpfulTipsProps) {
  const { isMobile } = useScreenSize();
  const theme = useTheme();

  return (
    <Card sx={{ backgroundColor: theme.palette.dataCard?.dark || 'initial' }}>
      <Title variant="body1" sx={{ color: theme.palette.success.main }}>
        {createElement(StyledIcon(Icons.Info))} Helpful Tips
      </Title>
      <Stack
        justifyContent="space-between"
        sx={() => ({
          display: 'flex',
          flexDirection: 'row',
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'center',
          },
        })}
      >
        {tips.map((tip) => (
          <Box
            width={200}
            marginTop={2}
            sx={() => ({
              [theme.breakpoints.down('sm')]: {
                display: 'flex',
                gap: theme.spacing(2.5),
                width: '280px',
              },
            })}
          >
            <img src={tip.icon} alt="Tip Icon" width={isMobile ? '40px' : ''} />
            <Text marginTop={1}>{tip.text}</Text>
          </Box>
        ))}
      </Stack>
    </Card>
  );
}
